import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  Card,
  CardHeader,
  CardContent,
  Tabs,
  Tab,
  InputAdornment,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { MdClose } from 'react-icons/md';
import ReactMarkdown from 'react-markdown';
import EditableSummary from '../../../Component/EditableSummary';
import { moment } from '../../../utils/timeUtils';

export default function AutoNotePanel({ meetingSummaries = [], phoneCallSummaries = [], isInterestsPage = false }) {
  const [autoNoteTab, setAutoNoteTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [meetingSummaryCount, setMeetingSummaryCount] = useState(3);
  const [phoneCallSummaryCount, setPhoneCallSummaryCount] = useState(3);

  // Reset counts when data changes
  useEffect(() => {
    setMeetingSummaryCount(3);
    setPhoneCallSummaryCount(3);
  }, [meetingSummaries, phoneCallSummaries]);

  // Filter meeting summaries
  const filteredMeetings = meetingSummaries.filter(summary => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      (summary.direction && summary.direction.toLowerCase().includes(searchLower)) ||
      (summary.zoomSummary && summary.zoomSummary.toLowerCase().includes(searchLower)) ||
      (summary.gptSummary && summary.gptSummary.toLowerCase().includes(searchLower)) ||
      (summary.events?.meetingStartedAt && moment(summary.events.meetingStartedAt).format('MM-DD-YYYY hh:mm').toLowerCase().includes(searchLower))
    );
  });

  // Filter phone call summaries
  const filteredCalls = phoneCallSummaries.filter(summary => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      (summary.caller && summary.caller.toLowerCase().includes(searchLower)) ||
      (summary.callee && summary.callee.toLowerCase().includes(searchLower)) ||
      (summary.zoomSummary && summary.zoomSummary.toLowerCase().includes(searchLower)) ||
      (summary.gptSummary && summary.gptSummary.toLowerCase().includes(searchLower)) ||
      (summary.events?.summaryReceivedAt && moment(summary.events.summaryReceivedAt).format('MM-DD-YYYY hh:mm').toLowerCase().includes(searchLower)) ||
      (summary.createdAt && moment(summary.createdAt).format('MM-DD-YYYY hh:mm').toLowerCase().includes(searchLower))
    );
  });

  return (
    <Box>
      {/* Tabs */}
      <Tabs
        value={autoNoteTab}
        onChange={(e, newValue) => setAutoNoteTab(newValue)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label={`Meeting (${meetingSummaries.length})`} />
        <Tab label={`Phone Call (${phoneCallSummaries.length})`} />
      </Tabs>

      {/* Search Box */}
      <Box sx={{ mb: 2, p: 1, background: '#f8f9fa', borderRadius: 1 }}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder={(isInterestsPage && autoNoteTab === 0)? "Search by summary, or date..." : "Search by caller, callee, summary, or date..."}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearchTerm('')}
                >
                  <MdClose />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '20px',
              backgroundColor: 'white',
            }
          }}
        />
      </Box>

      {/* Meeting Summaries Tab */}
      {autoNoteTab === 0 && (
        <Box sx={{ maxHeight: 'calc(100vh - 350px)', minHeight: 400, overflow: 'auto' }}>
          {filteredMeetings.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              {searchTerm ? `No meeting summaries found matching "${searchTerm}"` : 'No meeting summaries available'}
            </Typography>
          ) : (
            <>
              {searchTerm && (
                <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
                  Showing {Math.min(meetingSummaryCount, filteredMeetings.length)} of {filteredMeetings.length} results
                </Typography>
              )}
              {filteredMeetings
                .sort((a, b) => {
                  const dateA = a.events?.meetingStartedAt ? new Date(a.events.meetingStartedAt) : new Date(0);
                  const dateB = b.events?.meetingStartedAt ? new Date(b.events.meetingStartedAt) : new Date(0);
                  return dateB - dateA;
                })
                .slice(0, meetingSummaryCount)
                .map((summary, idx) => (
                  <Card key={summary._id || idx} sx={{ mb: 2, background: '#f5f6fa', border: '1px solid #d1d5db' }}>
                    <CardHeader
                      title={<Typography variant="h6" color="primary">Meeting Summary</Typography>}
                      sx={{ pb: 0 }}
                    />
                    <CardContent>
                      <Typography variant="subtitle2"><b>Topic:</b> {summary.direction}</Typography>
                      <Typography variant="subtitle2"><b>Date:</b> {summary.events?.meetingStartedAt ? moment(summary.events.meetingStartedAt).format('MM-DD-YYYY hh:mm') : '-'}</Typography>
                      {(summary.zoomSummary || summary.gptSummary) && (
                        <EditableSummary summary={summary} />
                      )}
                      {summary.recording_url && (
                        <Box mt={1}>
                          <a href={summary.recording_url} target="_blank" rel="noopener noreferrer">View Recording</a>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                ))}
              {meetingSummaryCount < filteredMeetings.length && (
                <Box display="flex" justifyContent="center" mb={2}>
                  <Button variant="outlined" onClick={() => setMeetingSummaryCount(c => Math.min(c + 3, filteredMeetings.length))}>
                    Load More Meeting Summaries
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      {/* Phone Call Summaries Tab */}
      {autoNoteTab === 1 && (
        <Box sx={{ maxHeight: 'calc(100vh - 350px)', minHeight: 400, overflow: 'auto' }}>
          {filteredCalls.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              {searchTerm ? `No phone call summaries found matching "${searchTerm}"` : 'No phone call summaries available'}
            </Typography>
          ) : (
            <>
              {searchTerm && (
                <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
                  Showing {Math.min(phoneCallSummaryCount, filteredCalls.length)} of {filteredCalls.length} results
                </Typography>
              )}
              {filteredCalls
                .sort((a, b) => new Date(b.events?.summaryReceivedAt || b.createdAt) - new Date(a.events?.summaryReceivedAt || a.createdAt))
                .slice(0, phoneCallSummaryCount)
                .map((summary, idx) => (
                  <Card key={summary._id || idx} sx={{ mb: 2, background: '#e6f7f7', border: '1px solid #7ed6df' }}>
                    <CardHeader
                      title={<Typography variant="h6" color="primary">Phone Call Summary</Typography>}
                      sx={{ pb: 0 }}
                    />
                    <CardContent>
                      <Typography variant="subtitle2"><b>Caller:</b> {summary.caller || '-'}</Typography>
                      <Typography variant="subtitle2"><b>Callee:</b> {summary.callee || '-'}</Typography>
                      <Typography variant="subtitle2"><b>Date:</b> {summary.events?.summaryReceivedAt
                        ? moment(summary.events.summaryReceivedAt).format('MM-DD-YYYY hh:mm')
                        : summary.createdAt
                          ? moment(summary.createdAt).format('MM-DD-YYYY hh:mm')
                          : '-'}</Typography>
                      {(summary.zoomSummary || summary.gptSummary) && (
                        <EditableSummary summary={summary} />
                      )}
                    </CardContent>
                  </Card>
                ))}
              {phoneCallSummaryCount < filteredCalls.length && (
                <Box display="flex" justifyContent="center" mb={2}>
                  <Button variant="outlined" onClick={() => setPhoneCallSummaryCount(c => Math.min(c + 3, filteredCalls.length))}>
                    Load More Phone Call Summaries
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      )}
    </Box>
  );
}